import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

import {
  FaBolt,
  FaHome,
  FaBuilding,
  FaRoad,
  FaHeart,
  FaShoppingCart,
  FaUserCircle,
  FaUser,
  FaSignOutAlt,
  FaChevronDown,
  FaSignInAlt,
  FaBoxOpen,
  FaEnvelope,
  FaLock,
  FaBars,
  FaTimes,
} from "react-icons/fa";

// --- RESPONSIVE STYLES ---
const getResponsiveStyles = (width = null) => {
  const screenWidth = width !== null ? width : window.innerWidth;

  // Breakpoint definitions aligned with requested ranges:
  // 320–488, 488–588, 588–748, 748–1080, 1080–1288, 1288–1530, 1530–1900, 1900+
  const isSmallMobile = screenWidth >= 320 && screenWidth < 488; // 320–488
  const isMobile = screenWidth >= 488 && screenWidth < 588; // 488–588
  const isLargeMobile = screenWidth >= 588 && screenWidth < 748; // 588–748
  const isTablet = screenWidth >= 748 && screenWidth < 1080; // 748–1080
  const isDesktop = screenWidth >= 1080 && screenWidth < 1288; // 1080–1288
  const isLargeDesktop = screenWidth >= 1288 && screenWidth < 1530; // 1288–1530
  const isXLargeDesktop = screenWidth >= 1530 && screenWidth < 1900; // 1530–1900
  const isXXLargeDesktop = screenWidth >= 1900; // 1900+

  // Combined checks
  const isMobileView = screenWidth < 748; // Show mobile menu & mobile profile dropdown below 748px
  const isTabletView = screenWidth >= 748 && screenWidth < 1080;
  const isDesktopView = screenWidth >= 1080;
  // Specific range for 320px-488px (mobile tab range)
  const isMobileRange = screenWidth >= 320 && screenWidth <= 488;

  // Dynamic header height – reused for positioning other elements (like dropdown)
  const headerHeight = isSmallMobile
    ? 65
    : isMobile
      ? 70
      : isLargeMobile
        ? 75
        : 80;

  return {
    header: {
      height: isSmallMobile
        ? "65px"
        : isMobile
          ? "70px"
          : isLargeMobile
            ? "75px"
            : "80px",
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: isSmallMobile
        ? "0 12px"
        : isMobile
          ? "0 16px"
          : isLargeMobile
            ? "0 20px"
            : isTablet
              ? "0 24px"
              : isDesktop
                ? "0 32px"
                : isLargeDesktop
                  ? "0 5%"
                  : isXLargeDesktop
                    ? "0 6%"
                    : "0 7%",
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: "all 0.3s ease",
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      boxSizing: "border-box",
    },
    brand: {
      fontSize: isSmallMobile
        ? "16px"
        : isMobile
          ? "18px"
          : isLargeMobile
            ? "20px"
            : isTablet
              ? "22px"
              : isDesktop
                ? "24px"
                : isLargeDesktop
                  ? "26px"
                  : isXLargeDesktop
                    ? "28px"
                    : "30px",
      fontWeight: "800",
      color: "#0F172A",
      display: "flex",
      alignItems: "center",
      gap: isSmallMobile
        ? "4px"
        : isMobile
          ? "6px"
          : isLargeMobile
            ? "7px"
            : "8px",
      cursor: "pointer",
      letterSpacing: "-0.5px",
      // Allow the brand to shrink on very small screens instead of forcing horizontal overflow
      flexShrink: isMobileRange ? 1 : 0,
      minWidth: 0,
      maxWidth: isMobileRange ? "52%" : undefined,
    },
    brandText: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
    },
    brandIcon: {
      color: "#D60000",
      fontSize: isSmallMobile
        ? "18px"
        : isMobile
          ? "20px"
          : isLargeMobile
            ? "22px"
            : isTablet
              ? "24px"
              : isDesktop
                ? "26px"
                : isLargeDesktop
                  ? "28px"
                  : "30px",
    },
    navContainer: {
      display: isMobileView ? "none" : "flex",
      gap: isTablet ? "6px" : isDesktop ? "8px" : "10px",
      background: "#F1F5F9",
      padding: isTablet ? "4px" : isDesktop ? "5px" : "6px",
      borderRadius: "14px",
      flexWrap: "wrap",
    },
    navLink: {
      fontSize: isTablet
        ? "12px"
        : isDesktop
          ? "13px"
          : isLargeDesktop
            ? "14px"
            : "15px",
      fontWeight: "600",
      color: "#64748B",
      cursor: "pointer",
      transition: "all 0.3s ease",
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      gap: isTablet ? "5px" : isDesktop ? "6px" : "8px",
      padding: isTablet ? "6px 12px" : isDesktop ? "8px 14px" : "8px 16px",
      borderRadius: "10px",
      whiteSpace: "nowrap",
    },
    navLinkActive: {
      color: "rgb(16, 96, 194)",
      background: "white",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    },
    actionContainer: {
      display: "flex",
      alignItems: "center",
      gap: isMobileRange
        ? isSmallMobile
          ? "6px"
          : "7px"
        : isLargeMobile
          ? "12px"
          : isTablet
            ? "14px"
            : isDesktop
              ? "16px"
              : "20px",
      flexShrink: 0,
      flexWrap: "nowrap",
    },
    mobileMenuButton: {
      display: isMobileView ? "flex" : "none",
      alignItems: "center",
      justifyContent: "center",
      width: isMobileRange ? (isSmallMobile ? "36px" : "38px") : "40px",
      height: isMobileRange ? (isSmallMobile ? "36px" : "38px") : "40px",
      borderRadius: "8px",
      border: "none",
      background: "#F1F5F9",
      color: "#0F172A",
      cursor: "pointer",
      fontSize: isMobileRange ? (isSmallMobile ? "18px" : "19px") : "20px",
      transition: "all 0.2s ease",
      flexShrink: 0,
      minWidth: isMobileRange ? (isSmallMobile ? "36px" : "38px") : "40px",
      WebkitTapHighlightColor: "transparent",
    },
    mobileMenu: {
      position: "fixed",
      top: isMobileRange
        ? isSmallMobile
          ? "65px"
          : "70px"
        : isLargeMobile
          ? "75px"
          : "80px",
      left: 0,
      right: 0,
      bottom: 0,
      background: "white",
      zIndex: 999,
      padding: isMobileRange
        ? isSmallMobile
          ? "16px 12px"
          : "18px 14px"
        : "20px",
      transform: "translateX(-100%)",
      transition: "transform 0.3s ease",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      width: "100%",
      boxSizing: "border-box",
    },
    mobileMenuOpen: {
      transform: "translateX(0)",
    },
    mobileNavContainer: {
      display: "flex",
      flexDirection: "column",
      gap: isMobileRange ? (isSmallMobile ? "10px" : "12px") : "12px",
      marginTop: isMobileRange ? (isSmallMobile ? "16px" : "18px") : "20px",
      width: "100%",
      boxSizing: "border-box",
    },
    mobileNavLink: {
      fontSize: isMobileRange ? (isSmallMobile ? "15px" : "16px") : "16px",
      fontWeight: "600",
      color: "#64748B",
      cursor: "pointer",
      transition: "all 0.3s ease",
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      gap: isMobileRange ? (isSmallMobile ? "12px" : "14px") : "12px",
      padding: isMobileRange
        ? isSmallMobile
          ? "16px 14px"
          : "16px 16px"
        : "14px 16px",
      borderRadius: "10px",
      background: "#F1F5F9",
      width: "100%",
      minHeight: isMobileRange ? "48px" : "48px",
      boxSizing: "border-box",
      WebkitTapHighlightColor: "transparent",
    },
    mobileNavLinkActive: {
      color: "#D60000",
      background: "#FEF2F2",
    },
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      zIndex: 998,
      display: "none",
      opacity: 0,
      transition: "opacity 0.3s ease",
    },
    overlayVisible: {
      display: "block",
      opacity: 1,
    },
    // Profile Dropdown Styles
    profileTrigger: {
      display: "flex",
      alignItems: "center",
      gap: isSmallMobile
        ? "4px"
        : isMobile
          ? "6px"
          : isLargeMobile
            ? "8px"
            : "10px",
      cursor: "pointer",
      padding: isSmallMobile
        ? "3px 6px"
        : isMobile
          ? "4px 8px"
          : isLargeMobile
            ? "5px 10px"
            : "6px 12px",
      borderRadius: "30px",
      transition: "0.2s",
      userSelect: "none",
      border: "1px solid transparent",
      backgroundColor: "transparent",
    },
    profileTriggerHover: { backgroundColor: "#F8FAFC", borderColor: "#E2E8F0" },
    dropdownMenu: {
      // Desktop / tablet: anchored dropdown under profile
      // Mobile (320–748px): turn into a fixed, full-width sheet so it doesn't overflow
      position: isMobileView ? "fixed" : "absolute",
      top: isMobileView
        ? `${headerHeight + 8}px`
        : isSmallMobile
          ? "60px"
          : isMobile
            ? "65px"
            : isLargeMobile
              ? "70px"
              : "75px",
      right: isMobileView
        ? "12px"
        : isSmallMobile
          ? "12px"
          : isMobile
            ? "16px"
            : isLargeMobile
              ? "20px"
              : isTablet
                ? "24px"
                : isDesktop
                  ? "32px"
                  : "5%",
      left: isMobileView ? "12px" : "auto",
      width: isMobileView ? "auto" : "280px",
      maxWidth: isMobileView ? "calc(100vw - 24px)" : "320px",
      maxHeight: isMobileView ? `calc(100vh - ${headerHeight + 24}px)` : "none",
      background: "white",
      borderRadius: "16px",
      boxShadow: "0 20px 40px -5px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1)",
      padding: "8px",
      zIndex: 1001,
      overflowY: "auto",
      animation: "fadeIn 0.2s ease-out",
      border: "1px solid #F1F5F9",
      boxSizing: "border-box",
    },
    dropdownItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 16px",
      fontSize: isSmallMobile ? "13px" : "14px",
      fontWeight: "600",
      color: "#475569",
      cursor: "pointer",
      borderRadius: "12px",
      transition: "0.2s",
      marginBottom: "2px",
    },
    userInfoBox: {
      padding: isSmallMobile ? "12px" : "16px",
      background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
      borderRadius: "12px",
      marginBottom: "8px",
      border: "1px solid #E2E8F0",
    },
  };
};

// --- SUB-COMPONENTS ---

const HeaderLink = ({
  icon: Icon,
  text,
  active,
  onClick,
  styles,
  windowWidth,
}) => {
  const responsiveStyles = styles || getResponsiveStyles(windowWidth);
  const width = windowWidth !== undefined ? windowWidth : window.innerWidth;
  const iconSize =
    width < 420
      ? "12px"
      : width < 520
        ? "13px"
        : width < 720
          ? "14px"
          : width < 1080
            ? "14px"
            : "16px";
  return (
    <Link
      to={`/category/${text.toLowerCase().replace("highways", "highway")}`}
      style={{
        ...responsiveStyles.navLink,
        ...(active ? responsiveStyles.navLinkActive : {}),
      }}
    >
      <Icon style={{ fontSize: iconSize }} /> {text}
    </Link>
  );
};

const ActionIcon = ({
  icon: Icon,
  badge,
  onClick,
  activeColor,
  windowWidth,
}) => {
  const [hover, setHover] = useState(false);
  const width = windowWidth !== undefined ? windowWidth : window.innerWidth;
  const isSmallMobile = width >= 320 && width < 488; // 320–488
  const isMobile = width >= 488 && width < 588; // 488–588
  const isLargeMobile = width >= 588 && width < 748; // 588–748
  const isMobileView = width < 748;
  const isMobileRange = width >= 320 && width <= 488;

  const iconSize = isMobileRange
    ? isSmallMobile
      ? "14px"
      : "15px"
    : isLargeMobile
      ? "16px"
      : "18px";
  const containerSize = isMobileRange
    ? isSmallMobile
      ? "32px"
      : "34px"
    : isLargeMobile
      ? "36px"
      : "40px";
  const badgeSize = isMobileRange
    ? isSmallMobile
      ? "14px"
      : "15px"
    : isLargeMobile
      ? "16px"
      : "18px";
  const badgeFontSize = isMobileRange
    ? isSmallMobile
      ? "8px"
      : "9px"
    : "10px";

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: containerSize,
        height: containerSize,
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: activeColor ? activeColor : hover ? "#D60000" : "#64748B",
        cursor: "pointer",
        background: hover ? "#FEF2F2" : "transparent",
        transition: "all 0.2s ease",
        position: "relative",
        flexShrink: 0,
      }}
    >
      <Icon style={{ fontSize: iconSize }} />
      {badge > 0 && (
        <div
          style={{
            position: "absolute",
            top: "-2px",
            right: "-2px",
            width: badgeSize,
            height: badgeSize,
            background: "#D60000",
            color: "white",
            fontSize: badgeFontSize,
            fontWeight: "bold",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            border: "2px solid white",
          }}
        >
          {badge}
        </div>
      )}
    </div>
  );
};

const ProfileMenu = ({ onNavigate, styles, windowWidth }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [hoverTrigger, setHoverTrigger] = useState(false);
  const menuRef = useRef(null);
  const width = windowWidth !== undefined ? windowWidth : window.innerWidth;
  const isSmallMobile = width >= 320 && width < 488; // 320–488
  const isMobile = width >= 488 && width < 588; // 488–588
  const isLargeMobile = width >= 588 && width < 748; // 588–748
  const isMobileView = width < 748;

  // Load User Data on Mount & Listen for Events
  useEffect(() => {
    const loadUser = () => {
      const storedData = localStorage.getItem("userData");
      if (storedData) {
        try {
          setUser(JSON.parse(storedData));
        } catch (e) {
          console.error("User data parse error", e);
        }
      } else {
        setUser(null);
      }
    };
    loadUser();

    // Listen to custom event "auth-change" dispatched by AuthPage
    window.addEventListener("auth-change", loadUser);
    window.addEventListener("storage", loadUser);

    // Close on click outside
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("auth-change", loadUser);
      window.removeEventListener("storage", loadUser);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("userData");
    localStorage.removeItem("user_identifier");
    localStorage.removeItem("cart");
    setUser(null);
    window.location.href = "/auth";
  };

  const handleNav = (route) => {
    setIsOpen(false);
    if (typeof onNavigate === "function") {
      onNavigate(route);
    } else {
      window.location.href = `/${route}`;
    }
  };

  const navigate = useNavigate();

  const handleNavChange = (page) => {
    setIsOpen(false);
    navigate(`/${page}`);
  };

  const MenuItem = ({ icon: Icon, text, danger, action }) => {
    const [hover, setHover] = useState(false);
    return (
      <div
        style={{
          ...styles.dropdownItem,
          background: hover ? (danger ? "#FEF2F2" : "white") : "transparent",
          color: danger ? "#D60000" : hover ? "#0F172A" : "#475569",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => {
          setIsOpen(false);
          action();
        }}
      >
        <Icon style={{ opacity: hover ? 1 : 0.7, fontSize: "16px" }} /> {text}
      </div>
    );
  };

  return (
    <div ref={menuRef} style={{ position: "relative" }}>
      {/* Trigger Button */}
      <div
        style={{
          ...styles.profileTrigger,
          ...(hoverTrigger || isOpen ? styles.profileTriggerHover : {}),
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setHoverTrigger(true)}
        onMouseLeave={() => setHoverTrigger(false)}
      >
        <div
          style={{
            width: isSmallMobile
              ? "24px"
              : isMobile
                ? "26px"
                : isLargeMobile
                  ? "28px"
                  : "32px",
            height: isSmallMobile
              ? "24px"
              : isMobile
                ? "26px"
                : isLargeMobile
                  ? "28px"
                  : "32px",
            borderRadius: "50%",
            background: user ? "#E0F2FE" : "#F1F5F9",
            color: user ? "#0284C7" : "#94A3B8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: isSmallMobile
              ? "10px"
              : isMobile
                ? "11px"
                : isLargeMobile
                  ? "12px"
                  : "14px",
            fontWeight: "700",
            flexShrink: 0,
          }}
        >
          {user && user.name ? user.name.charAt(0).toUpperCase() : <FaUser />}
        </div>
        {!isMobileView && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              lineHeight: "1.2",
            }}
          >
            <span
              style={{ fontSize: "13px", fontWeight: "700", color: "#0F172A" }}
            >
              {user && user.name
                ? user.name.split(" ")[0]
                : user
                  ? "User"
                  : "Account"}
            </span>
            <span style={{ fontSize: "11px", color: "#64748B" }}>
              {user ? "Member" : "Sign In"}
            </span>
          </div>
        )}
        {!isMobileView && (
          <FaChevronDown
            style={{ fontSize: "10px", color: "#94A3B8", marginLeft: "4px" }}
          />
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div style={styles.dropdownMenu}>
          {user ? (
            <>
              {/* User Info Box */}
              <div style={styles.userInfoBox}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "#0F172A",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      fontWeight: "700",
                    }}
                  >
                    {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div style={{ overflow: "hidden" }}>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "800",
                        color: "#0F172A",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user.name || "User"}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#0284C7",
                        fontWeight: "600",
                        background: "#E0F2FE",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        width: "fit-content",
                      }}
                    >
                      Verified Member
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748B",
                    borderTop: "1px dashed #CBD5E1",
                    paddingTop: "8px",
                    wordBreak: "break-all",
                  }}
                >
                  <FaEnvelope
                    style={{ marginRight: "6px", verticalAlign: "middle" }}
                  />
                  {user.email || "No Email"}
                </div>
              </div>

              {/* Menu Items */}
              <MenuItem
                icon={FaUser}
                text="My Profile"
                action={() => handleNavChange("profile")}
              />
              <MenuItem
                icon={FaBoxOpen}
                text="My Orders"
                action={() => handleNavChange("myorders")}
              />
              <MenuItem
                icon={FaLock}
                text="Change Password"
                action={() => handleNavChange("changepassword")}
              />
              <div
                style={{
                  height: "1px",
                  background: "#F1F5F9",
                  margin: "6px 0",
                }}
              ></div>
              <MenuItem
                icon={FaSignOutAlt}
                text="Sign Out"
                danger={true}
                action={handleLogout}
              />
            </>
          ) : (
            <>
              <div
                style={{
                  padding: "20px 16px",
                  textAlign: "center",
                  background: "#F8FAFC",
                  borderRadius: "12px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    fontWeight: "800",
                    fontSize: "16px",
                    color: "#0F172A",
                    marginBottom: "4px",
                  }}
                >
                  Welcome to CGRMART
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748B",
                    marginBottom: "12px",
                  }}
                >
                  Sign in to access your orders and profile.
                </div>
                <button
                  onClick={() => (window.location.href = "/auth")}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#D60000",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Sign In / Register
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
const apiClient = axios.create({
  timeout: 10000, // 10 second timeout
  headers: {
    "Content-Type": "application/json",
  },
});
let wishCount = 0;
let cartCount = 0;
// --- MAIN HEADER COMPONENT ---
const Header = ({
  currentView,
  activeCategory,
  onNavigate,
  onCategoryChange,
}) => {
  const [user, setUser] = useState(null);
  const [countData, setCountData] = useState({
    cart: 0,
    wishlist: 0,
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const mobileMenuRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 720) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("userData");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (err) {
        console.error("User parse error", err);
      }
    }
  }, []);

  // Load wishlist count from localStorage when user not logged in
  useEffect(() => {
    if (user?.email) return; // If logged in, API handles count

    const loadLocalWishlist = () => {
      const wishlist = JSON.parse(localStorage.getItem("wishlist")) || [];

      setCountData((prev) => ({
        ...prev,
        wishlist: wishlist.length,
      }));
    };

    loadLocalWishlist();

    window.addEventListener("wishlistUpdated", loadLocalWishlist);

    return () =>
      window.removeEventListener("wishlistUpdated", loadLocalWishlist);
  }, [user]);

  useEffect(() => {
    if (!user?.email) return;

    const fetchCounts = async () => {
      try {
        const API_BASE_URL = `${process.env.REACT_APP_API_URL}`;
        const PRODUCT_LIST_API = `${API_BASE_URL}/count-list?user_identifier=${user.email}`;
        const response = await apiClient.get(PRODUCT_LIST_API);
        setCountData({
          cart: response.data.cart_count,
          wishlist: response.data.wishlist_count,
          cart_products: response.data.cart_products,
        });
        // ✅ STORE IN SESSION
        sessionStorage.setItem(
          "cart_products",
          JSON.stringify(response.data.cart_products),
        );
      } catch (error) {
        console.error(error);
      }
    };

    fetchCounts(); // initial fetch
    const interval = setInterval(fetchCounts, 2000); // refresh every 10s
    return () => clearInterval(interval);
  }, [user]);

  // Close mobile menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest("[data-mobile-menu-button]")
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const handleNav = (route) => {
    setIsMobileMenuOpen(false);
    if (typeof onNavigate === "function") {
      onNavigate(route);
    } else {
      window.location.href = `/${route}`;
    }
  };
  const category =
    localStorage.getItem("activeCategory") ||
    (localStorage.setItem("activeCategory", "residential"), "residential");

  if (currentView !== "residential" && typeof onNavigate === "function") {
    onNavigate("residential");
  }

  const handleCatChange = (cat) => {
    localStorage.setItem("activeCategory", cat);

    // Update URL dynamically
    navigate(`/category/${cat}`);

    // Optional: send to parent
    if (typeof onCategoryChange === "function") {
      onCategoryChange(cat);
    }

    setIsMobileMenuOpen(false);
  };

  const styles = getResponsiveStyles(windowWidth);
  const isMobileRange = windowWidth >= 320 && windowWidth <= 488;

  const MobileHeaderLink = ({ icon: Icon, text, active, onClick }) => {
    const isSmallMobile = windowWidth >= 320 && windowWidth < 488; // 320–488
    const isMobile = windowWidth >= 488 && windowWidth < 588; // 488–588
    const isMobileRange = windowWidth >= 320 && windowWidth <= 488;
    const iconSize = isMobileRange ? (isSmallMobile ? "16px" : "18px") : "18px";
    return (
      <div
        onClick={onClick}
        style={{
          ...styles.mobileNavLink,
          ...(active ? styles.mobileNavLinkActive : {}),
        }}
      >
        <Icon
          style={{ fontSize: iconSize, flexShrink: 0, minWidth: iconSize }}
        />
        <span style={{ whiteSpace: "nowrap", flex: 1, display: "block" }}>
          {text}
        </span>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @media (max-width: 767px) {
            body.menu-open {
              overflow: hidden;
            }
          }

          /* Ensure carousel containers are visible and not hidden behind header */
          .carousel-container,
          .carousel-wrapper,
          .carousel-slides,
          .carousel-slide,
          .carousel-image {
            position: relative !important;
            z-index: 1 !important;
            visibility: visible !important;
            display: block !important;
            opacity: 1 !important;
          }

          /* Ensure inline styled carousel containers are visible */
          [style*="carouselContainer"],
          [style*="carouselTrack"],
          [style*="carouselSlide"] {
            position: relative !important;
            z-index: 1 !important;
            visibility: visible !important;
            display: block !important;
            opacity: 1 !important;
          }

          /* Carousel buttons and dots should be above slides */
          .carousel-button,
          .carousel-dots,
          .carousel-dot {
            z-index: 10 !important;
            position: relative !important;
          }

          /* Ensure header stays on top but doesn't hide carousel content */
          header[style*="position: fixed"] {
            z-index: 1000 !important;
          }

          /* Main content area should account for fixed header */
          .workspace,
          [style*="workspace"] {
            margin-top: 100px !important;
            padding-top: 20px !important;
          }
          
          @media (max-width: 768px) {
            .workspace,
            [style*="workspace"] {
              margin-top: 90px !important;
            }
          }
          
          @media (max-width: 600px) {
            .workspace,
            [style*="workspace"] {
              margin-top: 85px !important;
            }
          }
        `}
      </style>
      <header style={styles.header}>
        {/* BRAND */}
        <div style={styles.brand} onClick={() => navigate("/")}>
          <img
            src="/logo.png"
            alt="CGRMART Logo"
            style={{ width: "100%", height: "60px", marginRight: "8px" }}
          />
        </div>

        {/* NAVIGATION PILLS - Desktop/Tablet */}
        <nav style={styles.navContainer}>
          <HeaderLink
            icon={FaHome}
            text="Residential"
            active={
              (!currentView || currentView === "residential") &&
              activeCategory === "residential"
            }
            onClick={() => handleCatChange("residential")}
            styles={styles}
            windowWidth={windowWidth}
          />
          <HeaderLink
            icon={FaBuilding}
            text="Commercial"
            active={
              (!currentView || currentView === "residential") &&
              activeCategory === "commercial"
            }
            onClick={() => handleCatChange("commercial")}
            styles={styles}
            windowWidth={windowWidth}
          />
          <HeaderLink
            icon={FaRoad}
            text="Highways"
            active={
              (!currentView || currentView === "residential") &&
              activeCategory === "highway"
            }
            onClick={() => handleCatChange("highway")}
            styles={styles}
            windowWidth={windowWidth}
          />
        </nav>

        {/* ACTIONS */}
        <div style={styles.actionContainer} id="actionContainer">
          <ActionIcon
            icon={FaHeart}
            badge={countData.wishlist}
            activeColor={
              currentView === "wishlist"
                ? "#D60000"
                : countData.wishlist > 0
                  ? "#D60000"
                  : null
            }
            onClick={() => {
              setIsMobileMenuOpen(false);
              navigate("/wishlist");
            }}
            windowWidth={windowWidth}
          />
          <ActionIcon
            icon={FaShoppingCart}
            badge={countData.cart}
            activeColor={
              currentView === "cartlist"
                ? "#D60000"
                : countData.cart > 0
                  ? "#D60000"
                  : null
            }
            onClick={() => {
              setIsMobileMenuOpen(false);
              navigate("/cart");
              window.location.reload();
            }}
            windowWidth={windowWidth}
          />

          <ProfileMenu
            onNavigate={onNavigate}
            styles={styles}
            windowWidth={windowWidth}
          />

          {/* Mobile Menu Button - Show for screens < 720px */}
          <button
            data-mobile-menu-button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={styles.mobileMenuButton}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        style={{
          ...styles.overlay,
          ...(isMobileMenuOpen ? styles.overlayVisible : {}),
        }}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        style={{
          ...styles.mobileMenu,
          ...(isMobileMenuOpen ? styles.mobileMenuOpen : {}),
        }}
      >
        <div
          style={{
            fontSize: isMobileRange
              ? windowWidth < 420
                ? "17px"
                : "18px"
              : "18px",
            fontWeight: 700,
            color: "#0F172A",
            marginBottom: isMobileRange
              ? windowWidth < 420
                ? "14px"
                : "16px"
              : "16px",
            paddingBottom: isMobileRange
              ? windowWidth < 420
                ? "14px"
                : "16px"
              : "16px",
            borderBottom: "2px solid #E2E8F0",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          Categories
        </div>
        <div style={styles.mobileNavContainer}>
          <MobileHeaderLink
            icon={FaHome}
            text="Residential"
            active={
              (!currentView || currentView === "residential") &&
              activeCategory === "residential"
            }
            onClick={() => handleCatChange("residential")}
          />
          <MobileHeaderLink
            icon={FaBuilding}
            text="Commercial"
            active={
              (!currentView || currentView === "residential") &&
              activeCategory === "commercial"
            }
            onClick={() => handleCatChange("commercial")}
          />
          <MobileHeaderLink
            icon={FaRoad}
            text="Highways"
            active={
              (!currentView || currentView === "residential") &&
              activeCategory === "highway"
            }
            onClick={() => handleCatChange("highway")}
          />
        </div>
      </div>
    </>
  );
};

export default Header;
